import { useDraggable } from '@dnd-kit/core';
import { Button, Card, Flex, Typography } from 'antd';
import moment from 'moment';
import { getSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { LibraryContext } from './context-provider';
import FolderContextMenu from './folder-context-menu';
import styles from './styles.module.css';
const { Text } = Typography;

export default function FolderCard({ folder }) {
   const { selectedLibrary, setSelectedLibrary, library } = useContext(LibraryContext);
   const { attributes, listeners, setNodeRef, transition, isDragging } = useDraggable({
      id: folder._id,
      type: 'folder',
      data: { type: 'folder', ...folder },
   });
   const [permission, setPermission] = useState(null);

   const dragStyle = {
      transition,
      opacity: isDragging ? 0.5 : 1,
   };

   const router = useRouter();

   useEffect(() => {
      if (library?.data?.permission) setPermission(library?.data?.permission);
      else getPermission(folder).then((permission) => setPermission(permission));
   }, []);

   async function getPermission(folder) {
      // get current user id
      const user = (await getSession()).user;

      // check if folder creator is current user
      if (folder.creator === user?.id) return 'write';

      // check if folder is shared with current user
      const sharedWith = folder.sharedWith?.find((item) => item.email === user?.email);
      if (sharedWith) return sharedWith.permission;

      return false;
   }

   function handleFolderClick() {
      // if ctrl key or command key is pressed, add folder to selectedLibrary
      if (window.event.ctrlKey || window.event.metaKey) {
         // if folder is already selected, remove it from selectedLibrary
         if (selectedLibrary?.find((item) => item._id === folder._id)) {
            setSelectedLibrary(selectedLibrary?.filter((item) => item._id !== folder._id));
         } else {
            // add type property to folder and add it to selectedLibrary
            setSelectedLibrary([...selectedLibrary, { type: 'folder', ...folder }]);
         }
      }

      // if no key is pressed, select only the current folder
      else {
         setSelectedLibrary([{ type: 'folder', ...folder }]);
      }
   }

   function handleFolderDoubleClick() {
      // clear selectedLibrary
      setSelectedLibrary([]);
      // open folder page
      router.push(`/library/folder/${folder._id}`);
   }

   return (
      <>
         <FolderContextMenu trigger={['contextMenu']} permission={permission}>
            <Card
               onClickCapture={handleFolderClick}
               onContextMenu={() => setSelectedLibrary([{ type: 'folder', ...folder }])}
               onDoubleClickCapture={handleFolderDoubleClick}
               styles={{ body: {
                  padding: 16,
                  aspectRatio: '4/3.3',
               } }}
               className={`${styles.folderCard}   
            ${selectedLibrary?.find((item) => item._id === folder._id) && styles.selected}`}
               style={{ ...dragStyle }}
               {...attributes}
               {...listeners}
               ref={setNodeRef}
            >
               <Flex
                  vertical
                  gap={8}
                  justify={'space-between'}
                  style={{ height: '100%', position: 'relative' }}
               >
                  <FolderContextMenu trigger={['click']} permission={permission}>
                     <Button
                        icon={<i className="fi fi-rr-menu-dots-vertical" />}
                        style={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
                        type="text"
                     />
                  </FolderContextMenu>

                  <div className={styles.folderIcon} style={{ backgroundColor: folder.color || '#E6E8F0' }}>
                     <i className="fi fi-rr-folder" />
                  </div>

                  <Flex vertical gap={8} style={{ marginTop: 8 }}>
                     <Text strong style={{ fontSize: 14 }} ellipsis key={folder.name}>
                        {folder.name}
                     </Text>
                     <Text style={{ fontSize: 12 }} type="secondary">
                        {`created at ${moment(folder.createdAt).format('MMM YYYY')}`}
                     </Text>
                  </Flex>
               </Flex>
            </Card>
         </FolderContextMenu>
      </>
   );
}
